import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import styled from 'styled-components'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import Text from 'components/_UI/Text'
import { Link2 } from 'lucide-react'
import { FullyCentered, HorizontalCenter } from 'styled/alignment/Center'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent'
import teamsApi from 'api/requests/teams'

export default function TeamGrantForEventModal({ visible, event, onClose, onSuccess }) {
    const [teams, setTeams] = useState([]);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);

    useEffect(() => {
        fetchTeams();
    }, []);

    async function fetchTeams() {
        setLoadingTeams(true);

        try {
            const { data } = await teamsApi.get();
            const availableTeams = (data?.data || []).filter(team => !(team.grants || []).some(grant => {
                return grant.scope_type === 'panel' || (grant.scope_type === 'event' && grant.event?.id === event?.id);
            }));

            setTeams(availableTeams);
        } catch (error) {

        } finally {
            setLoadingTeams(false);
        }
    }

    async function onSubmit() {
        setErrorMessage(false);
        setLoading(true);

        try {
            await teamsApi.addGrant(selectedTeam?.value, {
                scope_type: 'event',
                event_id: event?.id,
            });
            onSuccess();
        } catch ({ response }) {
            setErrorMessage(response?.data?.message)
        } finally {
            setLoading(false);
        }
    }

    const teamOptions = teams.map(team => ({ value: team.id, label: team.name }));

    return (
        <Modal isOpen={visible} closeButton={true} onClose={onClose} size='medium'>
            <Container>
                <ModalHeader>
                    <IconCircle>
                        <Link2 size={20} color={panelAccent(colors.secondaryPurple)} />
                    </IconCircle>
                    <HeaderTitle>Vincular equipe a este evento</HeaderTitle>
                </ModalHeader>

                <Column marginTop={'20px'}>
                    <Text color={colors.night}>
                        Os membros da equipe passam a ter, neste evento, o que a permissão dela libera para um evento: validar, ver participantes e vendas e editar, com os cupons e as despesas dele. Financeiro e o resto do painel só valem com acesso ao "Painel inteiro".
                    </Text>

                    <FieldWrapper>
                        <SideTitle>Equipe</SideTitle>
                        <Select
                            placeholder={loadingTeams ? 'Carregando equipes...' : 'Selecione uma equipe'}
                            options={teamOptions}
                            value={selectedTeam}
                            onChange={setSelectedTeam}
                            isLoading={loadingTeams}
                            noOptionsMessage={() => 'Nenhuma equipe disponível — todas já têm acesso a este evento, ou você ainda não criou uma equipe'}
                        />
                    </FieldWrapper>

                    {
                        errorMessage && (
                            <HorizontalCenter marginTop={'10px'}>
                                <Text color={colors.danger}>
                                    {errorMessage}
                                </Text>
                            </HorizontalCenter>
                        )
                    }
                </Column>

                {
                    loading ? (
                        <FullyCentered height='60px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <ButtonsContainer>
                            <Button
                                variant={'light'}
                                category='roundedBorded'
                                onClick={onClose}
                            >
                                Cancelar
                            </Button>

                            <Button
                                variant={!selectedTeam ? 'disabled' : 'lightBlue'}
                                category='roundedBorded'
                                onClick={selectedTeam ? onSubmit : undefined}
                            >
                                Vincular
                            </Button>
                        </ButtonsContainer>
                    )
                }
            </Container>
        </Modal>
    )
}

const Container = styled(Column)`
    width: 100%;
    text-align: left;
    justify-content: flex-start;
    align-items: flex-start;
`;

const FieldWrapper = styled.div`
    width: 100%;
    margin-top: 14px;
`;

const ButtonsContainer = styled(Column)`
    margin-top: 20px;

    @media screen and (min-width: ${breakpoints.md}) {
        margin-top: 40px;
        flex-direction: row;

        button:first-child {
            margin-right: 16px;
        }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const IconCircle = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const SideTitle = styled.h4`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
    margin: 0 0 6px;
`;
